/**
 * platform/requests.ts — THE CONSOLE'S SEVEN REQUESTS, AS DATA.
 *
 * ===========================================================================
 * WHY THIS IS A PURE MODULE AND NOT A SET OF `fetch` CALLS
 * ===========================================================================
 * A console binding is the kind of code that gets written, looks right, and is
 * never executed against the thing it names. The ten routes this console drives
 * had ZERO client bindings for a month and nobody noticed, because there was
 * nothing to compare them against.
 *
 * So the request SHAPE is separated from the act of sending it. Every path,
 * method and body this application will ever produce is derived here, from
 * pure functions, which means:
 *
 *   - `console-binds-real-routes.test.ts` reads `knowledge/global/routes.ts`
 *     out of the API source tree and asserts that every path below is a route
 *     that module registers. A binding that names a path the server does not
 *     serve is caught at unit-test time rather than by an operator.
 *   - the same test asserts the reverse direction for the four ruled verbs, so
 *     a route family the console silently stopped covering is a failure and not
 *     a quiet omission.
 *
 * ===========================================================================
 * SEVEN REQUESTS FOR FOUR VERBS, AND WHY THE OTHER THREE ARE NOT PADDING
 * ===========================================================================
 * The founder named four acts: REVIEW, PUBLISH, SUPERSEDE, RETIRE. Three of
 * these seven are those acts (`review`, `publish`, `retire`); supersession is
 * not a fifth call — it is a property a proposal already carries and that
 * `publish` realises, which is why there is no `supersede()` here.
 *
 * The other four are reads, and each one is load-bearing for a decision the
 * ruling requires to be made honestly:
 *
 *   `corpus`        WHAT IS IN FORCE. Without it an operator cannot find the
 *                   requirement they mean to retire, and cannot see that the
 *                   one they are about to supersede was already withdrawn.
 *   `queue`         WHAT IS WAITING. The review queue itself.
 *   `document`      SOURCE AND PROVENANCE. Reviewing a proposal without seeing
 *                   the document it was drawn from, who ingested it, who
 *                   verified its identity and as what, is rubber-stamping.
 *   `sourceClasses` WHY A SOURCE MAY OR MAY NOT BECOME A FLOOR — including the
 *                   ineligible classes WITH their reason. An operator who
 *                   cannot see that `industry_summary` exists and can never
 *                   publish is an operator guessing, and guessing here is how a
 *                   blog gets declared a federal regulation.
 */

export interface ConsoleRequest {
  readonly method: 'GET' | 'POST';
  /** An absolute API path. Always begins `/api/system/global-knowledge/`. */
  readonly path: string;
  readonly body?: unknown;
}

/** The lifecycle states a published external requirement can be in. */
export type RequirementState = 'active' | 'superseded' | 'retired';

/** The states a proposal can be in. */
export type ProposalState = 'proposed' | 'approved' | 'rejected' | 'published' | 'withdrawn';

const BASE = '/api/system/global-knowledge';

function withState(path: string, state?: string): string {
  return state ? `${path}?state=${encodeURIComponent(state)}` : path;
}

/**
 * WHAT IS IN FORCE. An omitted `state` returns EVERY state, deliberately: a
 * corpus view filtered to `active` by default cannot tell an operator the
 * difference between "nothing was ever published for this subject" and
 * "everything published for it has been withdrawn".
 */
export function corpus(state?: RequirementState): ConsoleRequest {
  return { method: 'GET', path: withState(`${BASE}/requirements`, state) };
}

/** WHAT IS WAITING. Same rule about the default: every state, or none named. */
export function queue(state?: ProposalState): ConsoleRequest {
  return { method: 'GET', path: withState(`${BASE}/proposals`, state) };
}

/** SOURCE AND PROVENANCE for one ingested document. */
export function document(documentId: string): ConsoleRequest {
  return { method: 'GET', path: `${BASE}/documents/${encodeURIComponent(documentId)}` };
}

/** THE ELIGIBILITY VOCABULARY, ineligible classes and their reasons included. */
export function sourceClasses(): ConsoleRequest {
  return { method: 'GET', path: `${BASE}/source-classes` };
}

/**
 * REVIEW. A rejection carries the finding that produced it; the server refuses
 * one that does not, and so does this.
 */
export function review(
  proposalId: string,
  decision: 'approved' | 'rejected',
  note?: string,
): ConsoleRequest {
  return {
    method: 'POST',
    path: `${BASE}/proposals/${encodeURIComponent(proposalId)}/review`,
    // An omitted note is ABSENT, not empty. `''` would reach a column whose
    // CHECK rejects blank text, and if it ever got through it would be a
    // reviewer's reasoning recorded as having been given and been nothing.
    body: note && note.trim() ? { decision, note: note.trim() } : { decision },
  };
}

/**
 * PUBLISH — and SUPERSEDE, which is the same act.
 *
 * There is no `supersede()` function because supersession is not a separate
 * call. A proposal carries `supersedesRuleId` from the moment it is extracted;
 * publishing it is what closes the predecessor's window. The console's job is
 * to make that visible BEFORE the operator publishes — which is a rendering
 * responsibility (`describePublicationEffect`), not a second endpoint.
 */
export function publish(proposalId: string): ConsoleRequest {
  return {
    method: 'POST',
    path: `${BASE}/proposals/${encodeURIComponent(proposalId)}/publication`,
  };
}

/**
 * RETIRE. Both fields are required and neither is defaulted.
 *
 * `effectiveTo` is not "today". A repeal has a date and it is rarely today: a
 * regulator announces one effective next quarter, and an erroneous publication
 * stopped applying when somebody noticed. A default would put a date in the
 * audit trail that nobody assigned.
 */
export function retire(ruleId: string, effectiveTo: string, reason: string): ConsoleRequest {
  return {
    method: 'POST',
    path: `${BASE}/requirements/${encodeURIComponent(ruleId)}/retirement`,
    body: { effectiveTo, reason },
  };
}

/**
 * Every request shape the console can produce, with a fixed argument, for the
 * binding test to walk.
 *
 * It is a FUNCTION rather than a constant so the test cannot be satisfied by a
 * literal list that has drifted from the functions above — it calls the real
 * builders with real-shaped arguments and reads the paths they actually return.
 */
export function allRequestShapes(): ConsoleRequest[] {
  const id = '00000000-0000-4000-8000-000000000000';
  return [
    corpus(),
    corpus('retired'),
    queue(),
    queue('proposed'),
    document(id),
    sourceClasses(),
    review(id, 'approved'),
    review(id, 'rejected', 'the citation names a bulletin that was rescinded'),
    publish(id),
    retire(id, '2026-01-01', 'rescinded by the issuing authority'),
  ];
}
